import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { FileText, Pencil, Plus, Star } from "lucide-react";

type OfferSheet = {
  id: string;
  title: string;
  content: string;
  isActive: boolean;
  updatedAt: string | null;
};

/**
 * IdealClientSheetSection — arkusz idealnego klienta / ofertowy w Ustawieniach.
 * Zastępuje osobną zakładkę modułu offer-builder w sidebarze (wyjętą z nawigacji):
 * lista arkuszy, edycja treści, aktywacja. Aktywny arkusz jest kontekstem dla
 * kreatora grafik/kampanii (offerSheetContext) i tooli MCP.
 */
export function IdealClientSheetSection() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | "new" | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const { data: sheets = [], isLoading } = useQuery<OfferSheet[]>({
    queryKey: ["/api/offer-sheets"],
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/offer-sheets"] });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editingId === "new") {
        const sheet = (await apiRequest("POST", "/api/offer-sheets", { title, content })) as {
          id: string;
        };
        await apiRequest("PUT", `/api/offer-sheets/${sheet.id}/activate`, {});
      } else {
        await apiRequest("PUT", `/api/offer-sheets/${editingId}`, { title, content });
      }
    },
    onSuccess: () => {
      setEditingId(null);
      invalidate();
      toast({ title: "Arkusz zapisany" });
    },
    onError: () => toast({ title: "Nie udało się zapisać arkusza", variant: "destructive" }),
  });

  const activateMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("PUT", `/api/offer-sheets/${id}/activate`, {}),
    onSuccess: () => {
      invalidate();
      toast({ title: "Arkusz ustawiony jako aktywny" });
    },
  });

  const startEdit = (sheet: OfferSheet | null) => {
    if (sheet) {
      setEditingId(sheet.id);
      setTitle(sheet.title);
      setContent(sheet.content);
    } else {
      setEditingId("new");
      setTitle("");
      setContent("");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-white/60" />
          <h3 className="text-sm font-semibold text-white">Arkusz idealnego klienta</h3>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => startEdit(null)}
          className="border-white/20 text-white/70 hover:bg-white/10 text-xs"
        >
          <Plus className="w-3 h-3 mr-1" />
          Nowy arkusz
        </Button>
      </div>
      <p className="text-xs text-white/60">
        Opis oferty, grupy docelowej i USP — aktywny arkusz jest kontekstem dla kreatora grafik,
        kampanii i Claude (MCP).
      </p>

      {editingId !== null && (
        <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-3 space-y-2">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Tytuł arkusza (np. Oferta VP SaaS)"
            className="bg-black/50 border-white/20 text-white text-sm"
          />
          <textarea
            rows={8}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Co sprzedajesz, komu, za ile, jaka transformacja klienta, USP, ton marki…"
            className="w-full px-3 py-2 bg-black/50 border border-white/20 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-blue-500/50 resize-y"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={!title.trim() || !content.trim() || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              className="bg-[#ef3e29] hover:bg-[#d63723] text-white text-xs"
            >
              {saveMutation.isPending ? "Zapisywanie…" : "Zapisz"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditingId(null)}
              className="text-white/50 text-xs"
            >
              Anuluj
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-white/30 text-xs">Ładowanie…</p>
      ) : sheets.length === 0 ? (
        <p className="text-white/30 text-xs">
          Brak arkuszy — stwórz pierwszy, żeby kreator znał Twoją ofertę.
        </p>
      ) : (
        <div className="space-y-1.5">
          {sheets.map((sheet) => (
            <div
              key={sheet.id}
              className="flex items-center justify-between rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2"
            >
              <div className="min-w-0 flex items-center gap-2">
                <span className="text-sm text-white truncate">{sheet.title}</span>
                {sheet.isActive && (
                  <Badge className="text-[10px] bg-green-600/20 text-green-400 border-green-500/50">
                    Aktywny
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!sheet.isActive && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => activateMutation.mutate(sheet.id)}
                    className="text-white/50 hover:text-white text-xs"
                    title="Ustaw jako aktywny"
                  >
                    <Star className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => startEdit(sheet)}
                  className="text-white/50 hover:text-white text-xs"
                  title="Edytuj"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
